import { useState } from 'react';

import PropTypes from 'prop-types';

import AddToContactListModal from '#contactListFeature/components/AddToContactListModal/AddToContactListModal';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import { CONTACT_ENTITY_LABEL } from '#lib/constants/entityLabels';

import { useBulkDeleteCompanies } from '../../../models/companies/mutations';
import { useDeleteContact } from '../../../models/contacts/mutations';

const formatCount = (count) => count.toLocaleString('de-DE');
const contactsLabel = (count) => `${formatCount(count)} ${count === 1 ? 'Kontakt' : 'Kontakte'}`;

/**
 * Selection bars for the company table: deleting selected companies, and the actions for contacts
 * selected inside expanded rows. Company and contact selection are mutually exclusive, so at most
 * one bar renders (BulkActionsBar renders nothing at zero selection). Non-destructive company
 * actions live in CompanyScopeActions in the table's actions slot.
 */
const CompanyBulkActions = ({ companySelection, contactSelection }) => {
	const [openModal, setOpenModal] = useState(null);
	const close = () => setOpenModal(null);

	const bulkDeleteCompanies = useBulkDeleteCompanies();
	const deleteContact = useDeleteContact();

	const { count: companyCount, selectAllInFilter, toScope, clear: clearCompanies, entityLabel } = companySelection;
	const { selectedIds: contactIds, clear: clearContacts } = contactSelection;

	const handleDeleteCompanies = () =>
		bulkDeleteCompanies.mutate(toScope(), {
			onSuccess: () => {
				clearCompanies();
				close();
			},
		});

	// No bulk endpoint for contacts; the selection is limited to expanded rows of one page.
	const handleDeleteContacts = async () => {
		await Promise.allSettled(contactIds.map((id) => deleteContact.mutateAsync(id)));
		clearContacts();
		close();
	};

	const companiesLabel = `${formatCount(companyCount)} Unternehmen`;

	return (
		<>
			{contactIds.length === 0 && (
				<BulkActionsBar
					selectedCount={companyCount}
					entityLabel={entityLabel}
					onClear={clearCompanies}
					onDelete={() => setOpenModal('deleteCompanies')}
				/>
			)}
			{contactIds.length > 0 && (
				<BulkActionsBar
					selectedCount={contactIds.length}
					entityLabel={CONTACT_ENTITY_LABEL}
					onClear={clearContacts}
					onAddToContactList={() => setOpenModal('contactList')}
					onDelete={() => setOpenModal('deleteContacts')}
				/>
			)}

			<ConfirmModal
				isOpen={openModal === 'deleteCompanies'}
				onClose={close}
				onConfirm={handleDeleteCompanies}
				title={`${companiesLabel} löschen`}
				message={
					selectAllInFilter
						? `Alle ${companiesLabel}, die den aktuellen Filtern entsprechen, werden in den Papierkorb verschoben – auch die auf anderen Seiten. Fortfahren?`
						: `Möchten Sie ${companiesLabel} wirklich löschen? Die Unternehmen werden in den Papierkorb verschoben.`
				}
				confirmText="Löschen"
				isLoading={bulkDeleteCompanies.isPending}
			/>

			<ConfirmModal
				isOpen={openModal === 'deleteContacts'}
				onClose={close}
				onConfirm={handleDeleteContacts}
				title={`${contactsLabel(contactIds.length)} löschen`}
				message={`Möchten Sie ${contactsLabel(contactIds.length)} wirklich löschen? Die Kontakte werden in den Papierkorb verschoben.`}
				confirmText="Löschen"
				isLoading={deleteContact.isPending}
			/>

			{openModal === 'contactList' && (
				<AddToContactListModal
					isOpen
					onClose={close}
					contactPersonIds={contactIds}
					contactCount={contactIds.length}
					onSuccess={clearContacts}
				/>
			)}
		</>
	);
};

CompanyBulkActions.propTypes = {
	/** From useScopedSelection. */
	companySelection: PropTypes.shape({
		count: PropTypes.number.isRequired,
		selectAllInFilter: PropTypes.bool.isRequired,
		toScope: PropTypes.func.isRequired,
		clear: PropTypes.func.isRequired,
		entityLabel: PropTypes.object.isRequired,
	}).isRequired,
	contactSelection: PropTypes.shape({
		selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
		clear: PropTypes.func.isRequired,
	}).isRequired,
};

export default CompanyBulkActions;
